import React, { useState, useEffect } from 'react';
import { Button, ErrorMessage, FlexRow, Grid, Header4Text, ModalWindow, PageLoading } from 'ui-toolkit';
import { useGetACHIncomingActualQuery, usePostMatchACHIncomingMutation } from 'API/accounting/AccountingApi';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { createDateRangeFromDays } from 'utilities/accounting/createDateRangeFromDays';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { convertToError } from 'API/BaseApi';
import { IEscrowActualIncomingAchData } from 'types/accounting/data/IEscrowActualIncomingAchData';
import { IEscrowActualIncomingAchQuery } from 'types/accounting/query/IEscrowActualIncomingAchQuery';
import { MatchActualACHDepositTable } from './MatchActualACHDepositTable';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

export interface IMatchingACHModalSectionProps {
  expectedACH: IEscrowExpectedIncomingAchData;
  onClose: () => void;
}

export const MatchingACHModalSection: React.FC<IMatchingACHModalSectionProps> = ({ onClose, expectedACH }) => {
  const [selectedACH, setSelectedACH] = useState<IEscrowActualIncomingAchData | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const getDefaultACHQuery: () => IEscrowActualIncomingAchQuery = () => ({
    amountCents: expectedACH.amountCents,
    amountCentsMin: expectedACH.amountCents,
    createdAt: createDateRangeFromDays(-60),
    matched: eMatchedQueryValue.UNMATCHED,
    pageNumber: 1,
    pageSize: 25,
    sortField: 'createdAt',
    sortOrder: 'desc',
  });

  const [queryObject, setQueryObject] = useState(getDefaultACHQuery);

  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsRead]);
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualAchDepositsRead]);
  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualAchDepositsEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsEdit]);

  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const {
    data: getActualACH,
    isLoading: isLoadingGetActualACH,
    error: getActualACHError,
  } = useGetACHIncomingActualQuery(queryObject, {
    skip,
  });

  useEffect(() => {
    const newSelectedACH = selectedId && getActualACH && getActualACH.content.find((i) => i.id === selectedId);
    if (newSelectedACH && ((selectedACH && newSelectedACH.id !== selectedACH.id) || !selectedACH)) {
      setSelectedACH(newSelectedACH);
    }
  }, [getActualACH, selectedACH, selectedId]);

  const [doMatch, { error: matchError }] = usePostMatchACHIncomingMutation();
  const onMatch = async () => {
    if (!selectedACH || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selectedACH.id, expectedId: expectedACH.id });

    if (checkForMutationSuccess(result, 'matching ach')) {
      onClose();
    }
  };

  const isQueryValid =
    !queryObject.amountCents || !queryObject.amountCentsMin || queryObject.amountCents >= queryObject.amountCentsMin;

  return (
    <>
      {getActualACH && (
        <ModalWindow
          title={<Header4Text>Match an Actual ACH Deposit</Header4Text>}
          onClose={onClose}
          width={`90vw`}
          footer={
            <FlexRow justifyContent={'flex-end'}>
              <Grid columnCount={2} justify-self="end" columnGap={'spacing3'}>
                <Button intent={'ghost'} onClick={onClose} data-test-id={'cancel-button'}>
                  Cancel
                </Button>
                <Button intent={'primary'} disabled={!selectedACH} onClick={onMatch} data-test-id={'ach-match-button'}>
                  Match
                </Button>
              </Grid>
            </FlexRow>
          }
        >
          <PageLoading isLoading={isLoadingGetActualACH}>
            {!isQueryValid && (
              <ErrorMessage
                message={`The amount of the expected deposit must be less than the remaining amount of the incoming transaction.`}
              />
            )}
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={convertToError(getActualACHError) || convertToError(matchError)}
            />
            <MatchActualACHDepositTable
              data={getActualACH}
              isQueryFetching={isLoadingGetActualACH}
              query={queryObject}
              setQuery={setQueryObject}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
            />
          </PageLoading>
        </ModalWindow>
      )}
    </>
  );
};
